import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import {
  Tile, TileContent, Typography, TileMedia, TileText
} from '@one-thd/sui-atomic-components';
import {
  ConfirmedFilled, Delivery, DeliveryExpress, InStorePickup, Store
} from '@one-thd/sui-icons';
import { StoreSelector } from '../StoreSelector/StoreSelector';
import '../../styles/availability-filters.scss';
import { ProductResultsContext } from '../ProductResultsContext';

const AvailabilityRefinement = ({
  refinement,
  onDisplayData,
  active,
  isMobile,
  id,
  onChange,
  dimension
}) => {

  const { selected } = refinement;
  const { onDisplay, onDisplayChange, itemCount } = onDisplayData;
  const { data } = useContext(ProductResultsContext);
  const { isStoreDisplay } = data?.searchModel?.metadata || false;
  const fillOrange = '#F96302';

  const isTileSelected = selected || active;
  const isOnDisplayInUrl = onDisplay === 'true';

  const isOnDisplayTile = () => {
    return refinement?.label === 'Pick Up Today' && isStoreDisplay === 'true';
  };

  const isTileClicked = isOnDisplayTile() ? isOnDisplayInUrl : isTileSelected;
  const isUnavailable = isOnDisplayTile()
    ? parseInt(itemCount, 10) === 0 : parseInt(refinement?.recordCount, 10) === 0;

  const onChangeForOnDisplay = () => {
    // when onDisplay filters is selected/removed from leftNav/hortizontal-getItFast
    if (onDisplayChange) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements-visualNav.click', {
        dimensionName: 'Get It Fast',
        label: 'On Display',
        refinementLabel: 'On Display',
        refinementKey: '',
        selected: onDisplay === 'false' ? null : true
      });

      return (!!data && onDisplay === 'false') ? onDisplayChange({ useStoreFilter: 'true' })
        : onDisplayChange({ useStoreFilter: 'false' });
    }
    return null;
  };

  const onChangeForTileClick = () => {
    if (onChange) {
      if (isOnDisplayTile()) {
        onChangeForOnDisplay();
      } else {
        onChange({
          refinement, dimension, onDisplay
        });
      }
    }
  };

  const generateRefinementLabel = (isRefinementDisabled) => {
    // eslint-disable-next-line no-nested-ternary
    const displayLabel = refinement?.refinementKey === '1z175a5'
      ? isStoreDisplay === 'false' ? 'In Stock at Store Today' : 'On Display In Store'
      : refinement?.label;

    return (
      <Typography
        variant="body-base"
        color={isRefinementDisabled ? 'inactive' : 'primary'}
        align={isMobile ? 'center' : 'left'}
      >
        {displayLabel}
      </Typography>
    );
  };

  const generateIcon = () => {
    const { refinementKey } = refinement || null;
    if (refinementKey === '1z175a5' && isStoreDisplay === 'false') {
      return <InStorePickup style={{ fill: fillOrange }} />;
    }
    if (refinementKey === '1z175a5' && isStoreDisplay === 'true') {
      return <Store style={{ fill: fillOrange }} />;
    }
    if (refinementKey === 'bwo6i') {
      return <Delivery style={{ fill: fillOrange }} />;
    }
    if (refinementKey === '1z175cq') {
      return <DeliveryExpress style={{ fill: fillOrange }} />;
    }
    return null;
  };

  const refinementClassName = classNames('refinement__availability', {
    'refinement__availability--desktop': !isMobile,
    'refinement__availability--mini': isMobile,
    'refinement__availability--selected': isTileClicked,
    'refinement__availability--disabled': isUnavailable,
    'refinement__availability--default-border': isUnavailable || !isTileClicked
  });

  return (
    <>
      <Tile
        className={refinementClassName}
        value={refinement?.label}
        aria-label={refinement?.label}
        onClick={onChangeForTileClick}
        id={`${id}-${refinement?.label}`}
        key={`${id}-${refinement?.label}`}
        orientation="vertical"
        disabled={isUnavailable}
      >
        <TileContent
          className="refinement__availability--tile"
          orientation="horizontal"
          alignItems={isMobile ? 'center' : 'start'}
          disableGutters
        >
          <div className="refinement__availability--icon">
            {generateIcon()}
          </div>
          <div className="refinement__availability--label">
            <TileText>
              {generateRefinementLabel()}
            </TileText>
          </div>
          {isTileClicked && (
            <div className="refinement__availability--selected-icon">
              <ConfirmedFilled style={{ fill: fillOrange }} size="small" />
            </div>
          )}
        </TileContent>
      </Tile>
    </>
  );
};

AvailabilityRefinement.propTypes = {
  dimension: PropTypes.shape({}),
  refinement: PropTypes.shape({
    label: PropTypes.string,
    recordCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refinementKey: PropTypes.string,
    selected: PropTypes.bool,
    url: PropTypes.string,
  }),
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    itemCount: PropTypes.string
  }),
  active: PropTypes.bool,
  isMobile: PropTypes.bool,
  id: PropTypes.string,
  onChange: PropTypes.func
};

AvailabilityRefinement.defaultProps = {
  dimension: null,
  refinement: null,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    itemCount: null
  },
  active: false,
  isMobile: false,
  id: null,
  onChange: null
};

AvailabilityRefinement.displayName = 'AvailabilityRefinement';

AvailabilityRefinement.dataModel = extend(StoreSelector);

export { AvailabilityRefinement };
